// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useMemo, useState } from 'react';
import { stringify } from 'query-string';

import OffersComponent from 'Components/Offers/Offers';
import useAnalytics from 'Hooks/useAnalytics';
import useAsyncError from 'Hooks/useAsyncError';
import useFetch from 'Hooks/useFetch';
import useNumberUtils from 'Hooks/useNumberUtils';
import useOffers from 'Hooks/useOffers';
import useRouter from 'Hooks/useRouter';
import useUser from 'Hooks/useUser';
import { API_OFFERS } from 'Constants';
import isOfferExpired from 'Utils/offers';

const Offers = memo(() => {
  const analytics = useAnalytics();
  const fetcher = useFetch();
  const throwError = useAsyncError();
  const { formatDate, formatNumber } = useNumberUtils();
  const { location, push, query } = useRouter();
  const {
    data: { isImpersonated } = {},
    isLoading: userLoading
  } = useUser();
  const {
    offers,
    meta: privateOffersStorageMeta,
    activeOfferId,
    isHydrated,
    deleteOffer,
    setActiveOffer,
    setOffersPage
  } = useOffers();
  const [savedOffersData, setSavedOffersData] = useState([]);
  const [savedOffersMeta, setSavedOffersMeta] = useState({
    page: 1,
    limit: 20,
    totalPages: 0,
    totalItems: 0
  });
  const [savedOffersLoading, setSavedOffersLoading] = useState(false);
  const [savedOffersReload, setSavedOffersReload] = useState(0);
  const [offerToDelete, setOfferToDelete] = useState(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const isPrivateTab = location.pathname === '/offers/private';
  const savedOffersPage = parseInt(query?.page || 1, 10);

  useEffect(() => {
    const expirationTimer = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 30000);

    return () => window.clearInterval(expirationTimer);
  }, []);

  useEffect(() => {
    if (isPrivateTab) setOffersPage(savedOffersPage);
  }, [isPrivateTab, savedOffersPage, setOffersPage]);

  useEffect(() => {
    analytics.track('my_offers_tab_viewed', {
      tab: isPrivateTab ? 'private_browser' : 'saved_for_later'
    });
  }, [analytics, isPrivateTab]);

  useEffect(() => {
    if (isPrivateTab || userLoading || isImpersonated !== false) return undefined;

    let isActive = true;
    const path = `${ API_OFFERS }?${ stringify({
      page: savedOffersPage,
      limit: 20
    }) }`;
    const getSavedOffers = async (retryDedupedRequest = true) => {
      const response = await fetcher(path);

      if (!response && retryDedupedRequest && isActive) {
        return getSavedOffers(false);
      }

      return response;
    };

    setSavedOffersLoading(true);
    getSavedOffers()
      .then(response => {
        if (!isActive || !response) return;
        setSavedOffersData(response.items);
        setSavedOffersMeta(response.meta);
      })
      .catch(error => {
        if (error?.code !== 20) throwError(error);
      })
      .finally(() => {
        if (isActive) setSavedOffersLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [
    fetcher,
    isImpersonated,
    isPrivateTab,
    savedOffersPage,
    savedOffersReload,
    throwError,
    userLoading
  ]);

  const savedOffers = useMemo(
    () => savedOffersData.map(offer => ({
      ...offer,
      isExpired: isOfferExpired(offer, currentTime),
      clientDisplay: offer.clientName || '-',
      numberOfProducts: offer.numberOfProducts ?? offer.items?.length ?? 0,
      createdAtDisplay: offer.createdAt ? formatDate(offer.createdAt) : '-',
      updatedAtDisplay: offer.updatedAt ? formatDate(offer.updatedAt) : '-',
      validUntilDisplay: offer.validUntil ? formatDate(offer.validUntil) : '-',
      totalDisplay: formatNumber(offer.totalWithVat, {
        currency: (offer.currencyId || 'ron').toUpperCase(),
        maximumFractionDigits: 2
      })
    })),
    [currentTime, formatDate, formatNumber, savedOffersData]
  );

  const allPrivateOffers = useMemo(
    () => offers
      .filter(offer => offer.status === 'private')
      .sort((first, second) => new Date(second.updatedAt) - new Date(first.updatedAt))
      .map(offer => ({
        ...offer,
        isExpired: isOfferExpired(offer, currentTime),
        clientDisplay: offer.clientName || '-',
        createdAtDisplay: offer.createdAt ? formatDate(offer.createdAt) : '-',
        updatedAtDisplay: offer.updatedAt ? formatDate(offer.updatedAt) : '-',
        validUntilDisplay: offer.validUntil ? formatDate(offer.validUntil) : '-',
        totalDisplay: formatNumber(offer.totalWithVat, {
          currency: (offer.currencyId || 'ron').toUpperCase(),
          maximumFractionDigits: 2
        })
      })),
    [currentTime, formatDate, formatNumber, offers]
  );
  const privateOffersMeta = {
    ...privateOffersStorageMeta,
    page: Math.min(
      savedOffersPage,
      Math.max(privateOffersStorageMeta.totalPages, 1)
    )
  };
  const privateOffers = useMemo(() => {
    const start = (privateOffersMeta.page - 1) * privateOffersMeta.limit;
    return allPrivateOffers.slice(start, start + privateOffersMeta.limit);
  }, [allPrivateOffers, privateOffersMeta.limit, privateOffersMeta.page]);

  const handleOpen = offer => {
    analytics.track('my_offers_offer_opened', {
      offer_id: offer.id,
      product_count: offer.numberOfProducts,
      storage: offer.status === 'private' ? 'private_browser' : 'saved'
    });
    push(offer.status === 'private'
      ? `/offers/private/${ offer.id }/editor`
      : `/offers/${ offer.id }/editor`);
  };

  const handleDeleteRequest = offer => {
    analytics.track('my_offers_private_offer_delete_requested', {
      offer_id: offer.id,
      product_count: offer.numberOfProducts
    });
    setOfferToDelete(offer);
  };

  const reconcileActiveOfferAfterDelete = async deletedOfferId => {
    const remainingPrivateOffers = offers.filter(offer => (
      offer.status === 'private'
      && String(offer.id) !== String(deletedOfferId)
    ));
    let response;
    try {
      response = await fetcher(`${ API_OFFERS }?${ stringify({
        page: 1,
        limit: 20
      }) }`);
    } catch {
      if (String(activeOfferId) === String(deletedOfferId)) {
        setActiveOffer(null);
      }
      return;
    }
    if (!response) return;

    const remainingSavedOffers = (response.items || []).filter(
      offer => String(offer.id) !== String(deletedOfferId)
    );
    const savedOfferCount = Math.min(
      Number(response.meta?.totalItems || 0),
      remainingSavedOffers.length
    );
    const remainingOfferCount = remainingPrivateOffers.length + savedOfferCount;

    if (remainingOfferCount === 0) {
      setActiveOffer(null);
      return;
    }

    if (remainingOfferCount !== 1) {
      if (String(activeOfferId) === String(deletedOfferId)) {
        setActiveOffer(null);
      }
      return;
    }

    const remainingPrivateOffer = remainingPrivateOffers.at(0);
    const remainingSavedOffer = remainingSavedOffers.at(0);
    const remainingOffer = remainingPrivateOffer || remainingSavedOffer;
    if (!remainingOffer || isOfferExpired(remainingOffer)) {
      setActiveOffer(null);
      return;
    }

    setActiveOffer(
      remainingOffer.id,
      remainingPrivateOffer ? 'private' : 'saved'
    );
  };

  const handleDelete = async () => {
    if (!offerToDelete) return;
    const isPrivateOffer = offerToDelete.status === 'private';

    try {
      if (isPrivateOffer) {
        deleteOffer(offerToDelete.id);
        await reconcileActiveOfferAfterDelete(offerToDelete.id);
        if (privateOffers.length === 1 && privateOffersMeta.page > 1) {
          const nextQuery = { ...query, page: privateOffersMeta.page - 1 };
          push({
            pathname: location.pathname,
            search: stringify(nextQuery)
          });
        }
      } else {
        await fetcher(`${ API_OFFERS }/${ encodeURIComponent(offerToDelete.id) }`, {
          method: 'DELETE'
        });
        await reconcileActiveOfferAfterDelete(offerToDelete.id);
        if (savedOffersData.length === 1 && savedOffersMeta.page > 1) {
          const nextQuery = { ...query, page: savedOffersMeta.page - 1 };
          push({
            pathname: location.pathname,
            search: stringify(nextQuery)
          });
        } else {
          setSavedOffersReload(current => current + 1);
        }
      }
      analytics.track('my_offers_offer_deleted', {
        offer_id: offerToDelete.id,
        product_count: offerToDelete.numberOfProducts,
        storage: isPrivateOffer ? 'private_browser' : 'saved'
      });
      setOfferToDelete(null);
    } catch (error) {
      throwError(error);
    }
  };

  const handleOffersPageChange = ({ selected }) => {
    const page = selected + 1;
    const nextQuery = { ...query };

    if (page === 1) {
      delete nextQuery.page;
    } else {
      nextQuery.page = page;
    }

    push({
      pathname: location.pathname,
      search: stringify(nextQuery)
    });
  };

  const getOffersPageHref = page => stringify({
    ...query,
    page
  });

  return (
    <OffersComponent
      isHydrated={ isHydrated }
      isPrivateTab={ isPrivateTab }
      savedOffers={ savedOffers }
      savedOffersLoading={ savedOffersLoading || userLoading }
      savedOffersMeta={ savedOffersMeta }
      privateOffers={ privateOffers }
      privateOffersMeta={ privateOffersMeta }
      offerToDelete={ offerToDelete }
      onOpen={ handleOpen }
      onDeleteRequest={ handleDeleteRequest }
      onDeleteConfirm={ handleDelete }
      onDeleteCancel={ () => setOfferToDelete(null) }
      onOffersPageChange={ handleOffersPageChange }
      getOffersPageHref={ getOffersPageHref }
    />
  );
});

Offers.displayName = 'OffersContainer';

export default Offers;
